import toast from 'react-hot-toast';

export enum Issuer {
  Auth0 = 'Auth0',
  Firebase = 'Firebase',
  JWT = 'JWT',
  Amplify = 'Amplify',
}

// Global state for authentication and request management
class AuthManager {
  private static instance: AuthManager;
  private hasShown401 = false;
  private isUnauthorized = false;
  private pendingRequests = new Set<string>();
  private unauthorizedCallbacks: (() => void)[] = [];

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  // Handle 401 errors centrally
  handle401(): void {
    if (!this.hasShown401) {
      toast.error('Your session has expired. Please log in again.');
      this.hasShown401 = true;
      this.isUnauthorized = true;

      // Notify all subscribers about unauthorized state
      this.unauthorizedCallbacks.forEach((callback) => callback());
    }
  }

  // Check if currently unauthorized
  isCurrentlyUnauthorized(): boolean {
    return this.isUnauthorized;
  }

  // Reset unauthorized state (for when user logs in again)
  reset(): void {
    this.hasShown401 = false;
    this.isUnauthorized = false;
    this.pendingRequests.clear();
  }

  // Subscribe to unauthorized state changes
  onUnauthorized(callback: () => void): () => void {
    this.unauthorizedCallbacks.push(callback);
    return () => {
      const index = this.unauthorizedCallbacks.indexOf(callback);
      if (index > -1) {
        this.unauthorizedCallbacks.splice(index, 1);
      }
    };
  }

  // Prevent duplicate requests
  canMakeRequest(requestKey: string): boolean {
    if (this.isUnauthorized) {
      return false;
    }
    if (this.pendingRequests.has(requestKey)) {
      return false;
    }
    this.pendingRequests.add(requestKey);
    return true;
  }

  // Mark request as completed
  completeRequest(requestKey: string): void {
    this.pendingRequests.delete(requestKey);
  }

  // Handle any error and return whether it was a 401
  handleError(error: any, requestKey?: string): boolean {
    if (requestKey) {
      this.completeRequest(requestKey);
    }

    if (error?.response?.status === 401 || error?.status === 401) {
      this.handle401();
      return true;
    }
    return false;
  }
}

// Export singleton instance
export const authManager = AuthManager.getInstance();

// Legacy functions for backward compatibility

export const handle401 = () => {
  authManager.handle401();
};

export const handleThunkError = (error: any): boolean => {
  return authManager.handleError(error);
};
